import React, { useState, useEffect } from 'react';
import { ExtendedTheme, TextBlock, Chip } from 'neon-design-system';
import { Grid, createStyles, makeStyles, Paper } from '@material-ui/core';
import { ArticleSummary } from '../../../shared/types';
import { getLinkPrefix } from '../../../utils/list-utils';
import FetchMorePaginator from '../../ui/FetchMorePaginator';
import { PinnableArticlePreviewCard } from './ArticlePreviewCard';
import { getArticleLink } from '../../../utils/link.utils';
import { getPublishedData } from '../../ui/PublishedBy';
import { useArticleList } from '../../hooks/use-article-list';
import { NoArticlesPlaceholder } from './NoArticlesPlaceholder';
import { useOnDataLoaded } from '../../hooks/use-on-data-loaded';

const useStyles = makeStyles<ExtendedTheme>(theme => createStyles({
	caption: {
		marginTop: theme.spacing(1)
	},
	filter: {
		display: 'flex',
		gap: theme.spacing(1)
	},
	noArticlesFiltered: {
		paddingTop: `${theme.spacing(6)}px`,
		paddingBottom: `${theme.spacing(6)}px`
	}
}));

export interface BrowseArticleListProps {
	title: string;
	categoryId?: string;
	premiumChannelId?: string;
	showPlacement: boolean;
	showFilter?: boolean;
	displayInChartSection?: boolean;
	pinnedFirst?: boolean;
	onDataLoaded?: () => void;
}

enum FilterType { All, Premium, Free }

export const BrowseArticleList = (props: BrowseArticleListProps) => {
	const classes = useStyles();
	const batchSize = 10;
	const {categoryId, premiumChannelId, showFilter = false, displayInChartSection = false, pinnedFirst = false} = props;
	const queryArticles = useArticleList({ categoryId, premiumChannelId, batchSize, displayInChartSection, pinnedFirst });
	useOnDataLoaded(queryArticles.loading, props.onDataLoaded);
	const [filterType, setFilterType] = useState<FilterType>(FilterType.All);

	useEffect(() => {
		let freeOnly = undefined;
		let premiumOnly = undefined;

		if (filterType === FilterType.Premium) {
			premiumOnly = true;
		}
		if (filterType === FilterType.Free) {
			freeOnly = true;
		}

		queryArticles.refetch({ batchSize, categoryId, premiumChannelId, freeOnly, premiumOnly, displayInChartSection, pinnedFirst });
	}, [filterType])

	if (queryArticles.loading || !queryArticles.articles) return <></>;

	if (queryArticles.totalCount === 0 && props.showPlacement && !showFilter)
		return <NoArticlesPlaceholder isChannel={!!props.premiumChannelId} />;

	const showingCaption = `Showing ${queryArticles.articles.length} of ${queryArticles.totalCount} articles`;

	const filterStyle = (selectedFilterType: FilterType) =>
		selectedFilterType === filterType ? 'primary' : 'default';

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<TextBlock styleName="largeBody1">{props.title}</TextBlock>
				<TextBlock
					className={classes.caption}
					styleName="footnote"
					color="grey"
					shade={400}
				>
					{showingCaption}
				</TextBlock>
			</Grid>
			{showFilter && <Grid item xs={12}>
				<div className={classes.filter}>
					<Chip label="All articles" styleName={filterStyle(FilterType.All)} onClick={() => setFilterType(FilterType.All)} />
					<Chip label="Premium" styleName={filterStyle(FilterType.Premium)} onClick={() => setFilterType(FilterType.Premium)} />
					<Chip label="Free" styleName={filterStyle(FilterType.Free)} onClick={() => setFilterType(FilterType.Free)} />
				</div>
			</Grid>}
			{showFilter && queryArticles.totalCount === 0 && props.showPlacement ? (
				<Grid item xs={12}>
					<Paper className={classes.noArticlesFiltered}>
						<NoArticlesPlaceholder
							isChannel={!!props.premiumChannelId}
						/>
					</Paper>
				</Grid>
			) : <></>}
			<Grid item xs={12}>
				<Grid container>
					{queryArticles.articles.map(article =>
						<Grid item key={article._id} xs={12} md={12} style={{ paddingBottom: article.isPinned ? 16 : 0 }}>
							<PinnableArticlePreviewCard
								source="BrowseCategory"
								channel={article.premiumChannelDetails}
								title={article.title}
								summary={article.summary}
								articleLink={getArticleLink(article._id)}
								publishedData={getPublishedData((article as ArticleSummary), getLinkPrefix(), false, false)}
								isPinned={article.isPinned}
							/>
						</Grid>
					)}
				</Grid>
			</Grid>
			{
				!(queryArticles.totalCount === 0 && props.showPlacement) && showFilter && (
					<Grid item container xs={12} lg={12} justifyContent="space-around">
						<FetchMorePaginator
							hasNextPage={!!queryArticles.hasNextPage}
							label="articles"
							showingCurrent={queryArticles?.articles?.length}
							totalCount={queryArticles.totalCount || 0}
							fetchMore={queryArticles.fetchMore}
							displayShowMoreButton={true}
						/>
					</Grid>
				)
			}
		</Grid>
	);
}

export default BrowseArticleList;

